import { useState, useEffect } from 'react';
import { RefreshCw } from 'lucide-react';
import { API_BASE } from '../config';
import { formatDate, formatDateForFilename } from '../utils/formatDate';
import { hasEditAccess } from '../permissionConfig';
import { EMBEDDED_CSS, FONT, FluidStatusTabs, KpiBento, DmsHeader } from '../dmsTheme';
import DistributorSkuMasterTable from '../components/DistributorSkuMasterTable';

const DISTRIBUTOR = 'UPRISING DISTRIBUTION CO. GHZ.';

const STATUS_COLORS = {
    OUT:        { bg:'#FEE2E2', color:'#991B1B', label:'Out of Stock' },
    CRITICAL:   { bg:'#FEE2E2', color:'#991B1B', label:'Critical (<10d)' },
    LOW:        { bg:'#FEF3C7', color:'#92400E', label:'Low (10-20d)' },
    HEALTHY:    { bg:'#D1FAE5', color:'#065F46', label:'Healthy (20d+)' },
    NON_MOVING: { bg:'#E5E7EB', color:'#374151', label:'Non-Moving' },
};

const TABS = [
    { key: 'dashboard',  label: 'Dashboard' },
    { key: 'projection', label: '20-Day Projection' },
    { key: 'stock',      label: 'Stock Status' },
    { key: 'sales',      label: 'Sales Detail' },
    { key: 'sku_master', label: 'SKU Master' },
];

function exportXLSX(headers, dataRows, filename) {
    const XLSX = window.XLSX;
    if (!XLSX) { alert('SheetJS not loaded'); return; }
    const ws = XLSX.utils.aoa_to_sheet([headers, ...dataRows]);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Data');
    ws['!cols'] = headers.map(h => ({ wch: Math.max(String(h).length + 2, 14) }));
    XLSX.writeFile(wb, filename);
}

function today() { return new Date().toISOString().slice(0, 10); }

export default function DMSUprisingInventory({ user }) {
    const canEdit = hasEditAccess(user, 'dms', 'uprising_inventory');
    const [activeTab,       setActiveTab]       = useState('dashboard');
    const [dashboard,       setDashboard]       = useState(null);
    const [topAccounts,     setTopAccounts]     = useState([]);
    const [projection,      setProjection]      = useState(null);
    const [stockRows,       setStockRows]       = useState([]);
    const [loading,         setLoading]         = useState(true);
    const [uploadMsg,       setUploadMsg]       = useState(null);
    const [uploading,       setUploading]       = useState(false);
    const [projFilter,      setProjFilter]      = useState('all');
    const [skuSearch,       setSkuSearch]       = useState('');
    const [stockSearch,     setStockSearch]     = useState('');

    useEffect(() => { loadDashboard(); loadTopAccounts(); loadProjection(); loadStock(); }, []);

    async function loadDashboard() {
        setLoading(true);
        try {
            const res  = await fetch(`${API_BASE}/dms?action=dashboard&distributor=${encodeURIComponent(DISTRIBUTOR)}`);
            const data = await res.json();
            setDashboard(data);
        } catch(e) { console.error(e); }
        finally { setLoading(false); }
    }

    async function loadTopAccounts() {
        try {
            const res  = await fetch(`${API_BASE}/dms?action=top_accounts&distributor=${encodeURIComponent(DISTRIBUTOR)}`);
            const data = await res.json();
            setTopAccounts(Array.isArray(data) ? data : []);
        } catch(e) { console.error(e); }
    }

    async function loadProjection() {
        try {
            const res  = await fetch(`${API_BASE}/dms?action=projection&distributor=${encodeURIComponent(DISTRIBUTOR)}`);
            const data = await res.json();
            setProjection(data);
        } catch(e) { console.error(e); }
    }

    async function loadStock() {
        try {
            const res  = await fetch(`${API_BASE}/dms?action=stock&distributor=${encodeURIComponent(DISTRIBUTOR)}`);
            const data = await res.json();
            setStockRows(Array.isArray(data) ? data : []);
        } catch(e) { console.error(e); }
    }

    async function handleUpload(e, uploadAction) {
        const file = e.target.files[0];
        if (!file) return;
        setUploading(true); setUploadMsg(null);
        try {
            const text = await file.text();
            const res  = await fetch(`${API_BASE}/dms?action=${uploadAction}`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ html: text, distributor_name: DISTRIBUTOR, uploaded_by: user?.username || 'Portal User' })
            });
            const data = await res.json();
            if (res.ok && data.success) {
                setUploadMsg({ type:'success', text:`Uploaded ${data.inserted} rows for ${formatDate(data.report_date)}` });
                loadDashboard(); loadTopAccounts(); loadProjection(); loadStock();
            } else {
                setUploadMsg({ type:'error', text: data.message || 'Upload failed' });
            }
        } catch(err) {
            setUploadMsg({ type:'error', text:'Network error' });
        } finally { setUploading(false); }
        e.target.value = '';
    }

    const projRows     = projection?.rows || [];
    const filteredProj = projRows
        .filter(r => projFilter === 'all' || r.status === projFilter)
        .filter(r => !skuSearch || r.item_name.toLowerCase().includes(skuSearch.toLowerCase()));

    const filteredStock = stockRows.filter(r =>
        !stockSearch ||
        r.item_name?.toLowerCase().includes(stockSearch.toLowerCase()) ||
        r.ean?.toLowerCase().includes(stockSearch.toLowerCase())
    );

    return (
        <div data-tutorial="dms-uprising-table" className="dms-root" style={{ maxWidth: 1400, margin: '0 auto', background: 'var(--grn-page-bg)' }}>
            <style>{EMBEDDED_CSS}</style>
            <DmsHeader
                title="Uprising" accent="Distribution"
                subtitle={`${dashboard?.stock_kpi?.report_date ? `Stock as on ${formatDate(dashboard.stock_kpi.report_date)} · ` : ''}Data shown is for the current month`}
                right={<>
                    {uploadMsg && (
                        <div style={{ padding:'8px 16px', borderRadius:8, fontSize:13, fontWeight:600, background: uploadMsg.type==='success' ? 'rgba(92,126,96,0.25)' : 'rgba(194,94,119,0.25)', color: uploadMsg.type==='success' ? '#B8E0BC' : '#F3B8C4' }}>
                            {uploadMsg.text}
                        </div>
                    )}
                    <label style={{ padding:'9px 18px', background:'var(--grn-purple)', color:'#fff', borderRadius:8, fontSize:13, fontWeight:700, cursor:'pointer' }}>
                        {uploading ? 'Uploading...' : 'Upload Sales File'}
                        <input type="file" accept=".html,.htm" style={{ display:'none' }} onChange={e => handleUpload(e, 'upload_sales')} disabled={uploading} />
                    </label>
                    <label style={{ padding:'9px 18px', background:'#fff', color:'#0B0A12', borderRadius:8, fontSize:13, fontWeight:700, cursor:'pointer' }}>
                        {uploading ? 'Uploading...' : 'Upload Stock File'}
                        <input type="file" accept=".html,.htm" style={{ display:'none' }} onChange={e => handleUpload(e, 'upload_stock')} disabled={uploading} />
                    </label>
                    <button onClick={() => { loadDashboard(); loadTopAccounts(); loadProjection(); loadStock(); }}
                        style={{ display:'flex', alignItems:'center', gap:6, padding:'9px 14px', background:'rgba(255,255,255,0.1)', color:'#fff', border:'1px solid rgba(255,255,255,0.2)', borderRadius:8, fontSize:13, fontWeight:700, cursor:'pointer' }}>
                        <RefreshCw size={14} /> Refresh
                    </button>
                </>}
            />
            <div style={{ padding: '18px 22px 32px', display: 'flex', flexDirection: 'column', gap: 14 }}>
                <FluidStatusTabs tabs={TABS} value={activeTab} onChange={setActiveTab} />

                {loading ? (
                    <div style={{ padding:'60px', textAlign:'center', color:'var(--grn-text-muted)', fontSize:'16px' }}>Loading...</div>
                ) : (
                    <>
                        {/* ── DASHBOARD TAB ── */}
                        {activeTab === 'dashboard' && dashboard && (
                           <div>
                                <KpiBento items={[
                                    { label: 'Net Sales (Qty)', value: Number(dashboard.sales_kpi?.net_qty_sold || 0) },
                                    { label: 'Monthly Revenue', value: Number(dashboard.sales_kpi?.net_amount || 0), prefix: '₹' },
                                    { label: 'SOH Value', value: Number(dashboard.stock_kpi?.total_stock_value || 0), prefix: '₹' },
                                    { label: 'SOH Qty', value: Number(dashboard.stock_kpi?.total_stock_qty || 0) },
                                    { label: 'Zero Stock SKUs', value: Number(dashboard.stock_kpi?.zero_stock_skus || 0) },
                                ]} columns="repeat(5,1fr)" />

                                <div style={{ marginTop: 14 }}>
                                    <div style={cardS}>
                                        <div style={{ marginBottom:'16px' }}>
                                            <div style={{ fontSize:'16px', fontWeight:700, color:'var(--grn-text)' }}>Daily Net Sales <span style={{ fontSize: 11, fontWeight: 500, color: 'var(--grn-text-muted)' }}>(<strong>Days</strong> on X axis, <strong>Pcs</strong> on Y axis)</span></div>
                                        </div>
                                        {dashboard.daily_trend?.length > 0 ? (
                                            <div style={{ display:'flex', gap:'4px' }}>
                                                {(() => {
                                                    const maxQ = Math.max(...dashboard.daily_trend.map(x => x.qty), 1);
                                                    const minQ = Math.min(...dashboard.daily_trend.map(x => x.qty), 0);
                                                    const mid  = Math.round((maxQ + minQ) / 2);
                                                    return (
                                                        <div style={{ display:'flex', flexDirection:'column', justifyContent:'space-between', minWidth:'36px', height:'130px', paddingBottom:'18px' }}>
                                                            <div style={{ fontSize:'9px', color:'#000', fontWeight:700, textAlign:'right' }}>{maxQ}</div>
                                                            <div style={{ fontSize:'9px', color:'#000', fontWeight:700, textAlign:'right' }}>{mid}</div>
                                                            <div style={{ fontSize:'9px', color:'#000', fontWeight:700, textAlign:'right' }}>{minQ}</div>
                                                        </div>
                                                    );
                                                })()}
                                                <div style={{ flex:1 }}>
                                                    <div style={{ display:'flex', alignItems:'flex-end', gap:'3px', height:'110px' }}>
                                                        {(() => {
                                                            const maxQ = Math.max(...dashboard.daily_trend.map(x => Math.abs(x.qty)), 1);
                                                            return dashboard.daily_trend.map(d => {
                                                                const pct = (Math.abs(d.qty) / maxQ) * 100;
                                                                return (
                                                                    <div key={d.date} style={{ flex:1, display:'flex', flexDirection:'column', alignItems:'center', gap:'2px', height:'100%', justifyContent:'flex-end' }}>
                                                                        <div title={`${d.date}: ${d.qty} pcs`}
                                                                            style={{ width:'100%', height:`${Math.max(pct, 4)}%`, background: d.qty < 0 ? 'var(--grn-error)' : 'var(--grn-purple)', borderRadius:'3px 3px 0 0', cursor:'default', transition:'height 0.3s' }} />
                                                                    </div>
                                                                );
                                                            });
                                                        })()}
                                                    </div>
                                                    <div style={{ display:'flex', gap:'3px', marginTop:'6px' }}>
                                                       {dashboard.daily_trend.map((d) => (
                                                        <div key={d.date} style={{ flex:1, textAlign:'center' }}>
                                                         <div style={{ fontSize:'9px', color:'#000', fontWeight:700 }}>
                                                          {parseInt(d.date.slice(8,10), 10)}
                                                        </div>
                                                    </div>
                                                  ))}
                                               </div>
                                                </div>
                                            </div>
                                        ) : (
                                            <div style={{ textAlign:'center', color:'var(--grn-text-muted)', padding:'30px' }}>No sales data yet</div>
                                        )}
                                    </div>
                                </div>

                                {/* Top Accounts — normalized platform/region bento section, merges similar customer names (e.g. platform+region variants) into one bucket */}
                                <div style={{ ...cardS, marginTop: 14 }}>
                                    <div style={{ fontSize:'16px', fontWeight:700, color:'var(--grn-text)', marginBottom:'14px' }}>Top Accounts <span style={{ fontSize: 11, fontWeight: 500, color: 'var(--grn-text-muted)' }}>(Sales)</span></div>
                                    {topAccounts.length === 0 ? (
                                        <div style={{ textAlign:'center', color:'var(--grn-text-muted)', padding:'20px' }}>No account data yet</div>
                                    ) : (
                                        <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit, minmax(220px, 1fr))', gap: 12 }}>
                                            {topAccounts.map((a, i) => (
                                                <div key={i} style={{ background:'var(--grn-surface-subtle)', borderRadius: 12, padding: '14px 16px', border: '1px solid var(--grn-border)' }}>
                                                    <div style={{ fontSize: 12.5, fontWeight: 600, color:'var(--grn-text)', marginBottom: 6 }}>{a.account}</div>
                                                    <div style={{ fontSize: 18, fontWeight: 800, color:'var(--grn-purple)' }}>₹{Number(a.net_amount || 0).toLocaleString('en-IN')}</div>
                                                    <div style={{ fontSize: 11, color:'var(--grn-text-muted)', marginTop: 2 }}>{a.order_count} orders</div>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>

                                <div style={{ ...cardS, marginTop: 14 }}>
                                    <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:'14px' }}>
                                        <div style={{ fontSize:'16px', fontWeight:700, color:'var(--grn-text)' }}>Top SKUs by Sales</div>
                                        <button onClick={() => exportXLSX(
                                            ['EAN','SKU Name','Net Qty','Gross Qty','Returns','Net Revenue'],
                                            (dashboard.top_skus||[]).map(s=>[s.ean||'',s.item_name,s.net_qty_sold??s.qty_sold??0,s.gross_qty??0,s.return_qty??0,s.net_amount_sold??s.amount_sold??0]),
                                            `DMS_Dashboard_${today()}.xlsx`
                                        )} style={dlBtn}>⬇ Download</button>
                                    </div>
                                    <div style={{ overflowX:'auto' }}>
                                        <table style={tableS}>
                                            <thead>
                                                <tr>
                                                    {['Sr. no.','EAN','SKU Name','Net Qty','Net Revenue'].map(h => (
                                                        <th key={h} style={thS}>{h}</th>
                                                    ))}
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {dashboard.top_skus?.map((s, i) => (
                                                    <tr key={i} style={{ background: i%2===0?'var(--grn-surface)':'var(--grn-surface-subtle)' }}>
                                                        <td style={td}>{i+1}</td>
                                                        <td style={{ ...td, fontFamily:'monospace', fontSize:'12px', color:'var(--grn-purple)' }}>{s.ean || <span style={{color:'var(--grn-border-soft)'}}>—</span>}</td>
                                                        <td style={{ ...td, fontWeight:500, maxWidth:'240px' }}>{s.item_name}</td>
                                                        <td style={{ ...td, fontWeight:700, color:(s.net_qty_sold??s.qty_sold??0)<0?'var(--grn-error)':'var(--grn-blue)' }}>{Number(s.net_qty_sold??s.qty_sold??0).toLocaleString('en-IN')}</td>
                                                        <td style={{ ...td, color:'var(--grn-green)', fontWeight:600 }}>₹{Number(s.net_amount_sold??s.amount_sold??0).toLocaleString('en-IN')}</td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* ── PROJECTION TAB ── */}
                        {activeTab === 'projection' && (
                            <div>
                                <div style={{ ...cardS, marginBottom:'16px', display:'flex', justifyContent:'space-between', alignItems:'center', flexWrap:'wrap', gap:'12px' }}>
                                    <div>
                                        <div style={{ fontSize:'16px', fontWeight:700, color:'var(--grn-text)' }}>20-Day Inventory Projection</div>
                                        <div style={{ fontSize:'13px', color:'var(--grn-text-muted)', marginTop:'2px' }}>
                                            Based on avg daily net sales · Stock date: {formatDate(projection?.stock_date)}
                                        </div>
                                    </div>
                                    <div style={{ display:'flex', gap:'8px', flexWrap:'wrap', alignItems:'center' }}>
                                        <button onClick={() => exportXLSX(
                                            ['EAN','SKU Name','Current Stock','Avg Daily Sales','Needed 20 Days','Gap','Days of Stock','Status'],
                                            filteredProj.map(r=>[r.ean||'',r.item_name,r.current_stock,r.avg_daily_sales,r.needed_20_days,r.gap,r.days_of_stock??'No Sales',r.status]),
                                            `DMS_Projection_${today()}.xlsx`
                                        )} style={dlBtn}>⬇ Download</button>
                                        <input type="text" placeholder="Search SKU..." style={inputS}
                                            value={skuSearch} onChange={e => setSkuSearch(e.target.value)} />
                                        {['all','NON_MOVING','CRITICAL','LOW','HEALTHY','OUT'].map(s => (
                                         <button key={s} onClick={() => setProjFilter(s)}
                                          style={{ padding:'7px 14px', borderRadius:'20px', border: projFilter===s?'none':'1.5px solid var(--grn-border)', background: projFilter===s?(STATUS_COLORS[s]?.bg||'var(--grn-accent-surface)'):'var(--grn-surface)', color: projFilter===s?(STATUS_COLORS[s]?.color||'#fff'):'var(--grn-text-muted)', fontSize:'12px', fontWeight:600, cursor:'pointer' }}>
                                          {s === 'all' ? 'All' : STATUS_COLORS[s]?.label || s}
                                           </button>
                                         ))}
                                    </div>
                                </div>

                                <div style={{ display:'grid', gridTemplateColumns:'repeat(5,1fr)', gap:'12px', marginBottom:'16px' }}>
                                 {Object.entries(STATUS_COLORS).map(([status, cfg]) => {
                                  const count = projRows.filter(r => r.status === status).length;
                                 return (
                                   <div key={status} style={{ background:'var(--grn-surface)', borderRadius:'10px', padding:'14px 16px', boxShadow:'var(--grn-shadow-sm)', border:`2px solid ${cfg.bg}` }}>
                                     <div style={{ fontSize:'11px', fontWeight:700, color:cfg.color, textTransform:'uppercase', letterSpacing:'0.5px' }}>{cfg.label}</div>
                                     <div style={{ fontSize:'28px', fontWeight:800, color:cfg.color, marginTop:'4px' }}>{count}</div>
                                     <div style={{ fontSize:'11px', color:'var(--grn-text-muted)' }}>SKUs</div>
                                     </div>
                                     );
                                   })}
                                </div>

                                <div style={{ background:'var(--grn-surface)', borderRadius:'16px', boxShadow:'var(--grn-shadow-sm)', border:'1px solid var(--grn-border)', overflow:'hidden' }}>
                                    <div style={{ overflowX:'auto' }}>
                                        <table style={tableS}>
                                            <thead>
                                                <tr>
                                                    {['EAN','SKU Name','Current Stock','In Transit','Avg Daily Sales','Needed (20 Days)','Gap','Days of Stock','Status'].map(h => (
                                                        <th key={h} style={thS}>{h}</th>
                                                    ))}
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {filteredProj.length === 0 ? (
                                                    <tr><td colSpan={9} style={{ padding:'40px', textAlign:'center', color:'var(--grn-text-muted)' }}>No SKUs match filter</td></tr>
                                                ) : filteredProj.map((r, i) => {
                                                    const sc = STATUS_COLORS[r.status] || { bg:'var(--grn-surface-subtle)', color:'var(--grn-text-muted)' };
                                                    return (
                                                        <tr key={i} style={{ background: i%2===0?'var(--grn-surface)':'var(--grn-surface-subtle)' }}>
                                                            <td style={{ ...td, fontFamily:'monospace', fontSize:'12px', color:'var(--grn-purple)' }}>{r.ean || <span style={{color:'var(--grn-border-soft)'}}>—</span>}</td>
                                                            <td style={{ ...td, maxWidth:'220px', whiteSpace:'normal', fontWeight:500 }}>{r.item_name}</td>
                                                            <td style={{ ...td, fontWeight:700 }}>{Number(r.current_stock).toLocaleString('en-IN')}</td>
                                                            <td style={{ ...td, color: r.in_transit_qty > 0 ? '#1D4ED8' : 'var(--grn-text-muted)' }} title={r.in_transit_po ? `PO: ${r.in_transit_po}` : undefined}>{r.in_transit_qty > 0 ? `${Number(r.in_transit_qty).toLocaleString('en-IN')}${r.in_transit_date ? ` (${r.in_transit_date})` : ''}` : '—'}</td>
                                                            <td style={{ ...td, color:'var(--grn-text)' }}>{r.avg_daily_sales}/day</td>
                                                            <td style={{ ...td, fontWeight:700, color:'var(--grn-blue)' }}>{r.needed_20_days}</td>
                                                            <td style={{ ...td, fontWeight:700, color: r.gap > 0 ? 'var(--grn-error)' : 'var(--grn-green)' }}>
                                                                {r.gap > 0 ? `+${r.gap} needed` : `${Math.abs(r.gap)} surplus`}
                                                            </td>
                                                            <td style={{ ...td, fontWeight:700 }}>
                                                                {r.days_of_stock === null ? '∞' : `${r.days_of_stock}d`}
                                                            </td>
                                                            <td style={td}>
                                                                <span style={{ fontSize:'11px', fontWeight:700, padding:'3px 10px', borderRadius:'5px', background:sc.bg, color:sc.color, whiteSpace:'nowrap' }}>
                                                                    {r.status}
                                                                </span>
                                                            </td>
                                                        </tr>
                                                    );
                                                })}
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* ── STOCK STATUS TAB ── */}
                        {activeTab === 'stock' && (
                            <div>
                                <KpiBento items={[
                                    { label: 'SOH Qty', value: stockRows.reduce((s, r) => s + (Number(r.quantity) || 0), 0) },
                                    { label: 'SOH Amount', value: stockRows.reduce((s, r) => s + (Number(r.amount) || 0), 0), prefix: '₹' },
                                    { label: 'Zero Stock SKUs', value: stockRows.filter(r => Number(r.quantity) === 0).length },
                                ]} columns="repeat(auto-fit, minmax(220px, 1fr))" />
                                <div style={{ margin:'14px 0', display:'flex', justifyContent:'space-between', alignItems:'center', flexWrap:'wrap', gap:'10px' }}>
                                    <span style={{ fontSize:'14px', color:'var(--grn-text-muted)' }}>
                                        Report date: <strong>{formatDate(stockRows[0]?.report_date)}</strong>
                                    </span>
                                    <input type="text" placeholder="Search item or EAN..." style={inputS}
                                        value={stockSearch} onChange={e => setStockSearch(e.target.value)} />
                                </div>
                                <div style={{ background:'var(--grn-surface)', borderRadius:'16px', boxShadow:'var(--grn-shadow-sm)', border:'1px solid var(--grn-border)', overflow:'hidden' }}>
                                    <div style={{ overflowX:'auto' }}>
                                        <table style={tableS}>
                                            <thead>
                                                <tr>
                                                    {['Sr. no.','EAN','Item Name','Quantity','In Transit','Unit','Stock Value'].map(h => (
                                                        <th key={h} style={thS}>{h}</th>
                                                    ))}
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {filteredStock.length === 0 ? (
                                                    <tr><td colSpan={7} style={{ padding:'40px', textAlign:'center', color:'var(--grn-text-muted)' }}>{stockRows.length === 0 ? 'Upload a stock status file to see data' : 'No SKUs match filter'}</td></tr>
                                                ) : filteredStock.map((r, i) => (
                                                    <tr key={i} style={{ background: i%2===0?'var(--grn-surface)':'var(--grn-surface-subtle)' }}>
                                                        <td style={{ ...td, color:'var(--grn-text-faint)', fontSize:'12px' }}>{i+1}</td>
                                                        <td style={{ ...td, fontFamily:'monospace', fontSize:'12px', color:'var(--grn-purple)' }}>{r.ean || <span style={{color:'var(--grn-border-soft)'}}>—</span>}</td>
                                                        <td style={{ ...td, fontWeight:500, maxWidth:'280px' }}>{r.item_name}</td>
                                                        <td style={{ ...td, fontWeight:700, color: r.quantity === 0 ? 'var(--grn-error)' : 'var(--grn-text)' }}>{Number(r.quantity).toLocaleString('en-IN')}</td>
                                                        <td style={{ ...td, color: r.in_transit_qty > 0 ? '#1D4ED8' : 'var(--grn-text-muted)' }} title={r.in_transit_po ? `PO: ${r.in_transit_po}` : undefined}>{r.in_transit_qty > 0 ? `${Number(r.in_transit_qty).toLocaleString('en-IN')}${r.in_transit_date ? ` (${r.in_transit_date})` : ''}` : '—'}</td>
                                                        <td style={td}>{r.unit}</td>
                                                        <td style={{ ...td, color:'var(--grn-green)', fontWeight:600 }}>₹{Number(r.amount||0).toLocaleString('en-IN')}</td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* ── SALES DETAIL TAB ── */}
                        {activeTab === 'sales' && (
                            <SalesDetail distributor={DISTRIBUTOR} />
                        )}

                        {/* ── SKU MASTER TAB ── */}
                        {activeTab === 'sku_master' && (
                            <DistributorSkuMasterTable
                                nameCol="uprising_name"
                                nameLabel="Uprising Name"
                                canEdit={canEdit}
                                endpoint="/dms"
                                updatedBy={user?.username || 'Portal'}
                            />
                        )}
                    </>
                )}
            </div>
        </div>
    );
}

function SalesDetail({ distributor }) {
    const [rows,    setRows]    = useState([]);
    const [loading, setLoading] = useState(true);
    const [search,  setSearch]  = useState('');

    useEffect(() => {
        fetch(`${API_BASE}/dms?action=sales&distributor=${encodeURIComponent(distributor)}`)
            .then(r => r.json())
            .then(data => { setRows(Array.isArray(data) ? data : []); setLoading(false); })
            .catch(() => setLoading(false));
    }, []);

    const filtered = rows.filter(r =>
        !search ||
        r.item_name?.toLowerCase().includes(search.toLowerCase()) ||
        r.particulars?.toLowerCase().includes(search.toLowerCase()) ||
        r.ean?.toLowerCase().includes(search.toLowerCase())
    );

    function dlSales() {
        exportXLSX(
            ['EAN','Date','Bill No','Customer','PO No','Item Name','MRP','Qty','Price','Amount','Type'],
            filtered.map(r=>[r.ean||'',r.sale_date,r.vch_bill_no,r.particulars,r.po_number||'',r.item_name,r.mrp,r.quantity,r.price,r.amount,r.is_credit_note?'Credit':'Sale']),
            `DMS_Sales_${formatDateForFilename()}.xlsx`
        );
    }

    return (
        <div>
            <div style={{ marginBottom:'14px', display:'flex', justifyContent:'space-between', alignItems:'center', gap:'10px', flexWrap:'wrap' }}>
                <div style={{ display:'flex', gap:'10px', alignItems:'center' }}>
                    <input type="text" placeholder="Search SKU, customer or EAN..." style={inputS}
                        value={search} onChange={e => setSearch(e.target.value)} />
                    <span style={{ alignSelf:'center', fontSize:'13px', color:'var(--grn-text-muted)' }}>{filtered.length} rows</span>
                </div>
                <button onClick={dlSales} style={dlBtn}>⬇ Download</button>
            </div>
            <div style={{ background:'var(--grn-surface)', borderRadius:'16px', boxShadow:'var(--grn-shadow-sm)', border:'1px solid var(--grn-border)', overflow:'hidden' }}>
                {loading ? (
                    <div style={{ padding:'40px', textAlign:'center', color:'var(--grn-text-muted)' }}>Loading...</div>
                ) : (
                    <div style={{ overflowX:'auto', maxHeight:'600px', overflowY:'auto' }}>
                        <table style={tableS}>
                            <thead style={{ position:'sticky', top:0 }}>
                                <tr>
                                    {['Date','Bill No','Customer','PO No','EAN','Item Name','MRP','Qty','Price','Amount','Type'].map(h => (
                                        <th key={h} style={thS}>{h}</th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                {filtered.map((r, i) => (
                                    <tr key={i} style={{ background: r.is_credit_note ? 'var(--grn-pink-tint)' : i%2===0?'var(--grn-surface)':'var(--grn-surface-subtle)' }}>
                                        <td style={td}>{formatDate(r.sale_date)}</td>
                                        <td style={{ ...td, fontSize:'11px', fontFamily:'monospace', color:'var(--grn-text-muted)' }}>{r.vch_bill_no}</td>
                                        <td style={{ ...td, fontWeight:500, maxWidth:'160px', whiteSpace:'normal' }}>{r.particulars}</td>
                                        <td style={{ ...td, fontSize:'11px', color:'var(--grn-text-faint)' }}>{r.po_number || '—'}</td>
                                        <td style={{ ...td, fontFamily:'monospace', fontSize:'12px', color:'var(--grn-purple)' }}>{r.ean || <span style={{color:'var(--grn-border-soft)'}}>—</span>}</td>
                                        <td style={{ ...td, maxWidth:'180px', whiteSpace:'normal' }}>{r.item_name}</td>
                                        <td style={td}>₹{r.mrp}</td>
                                        <td style={{ ...td, fontWeight:700, color: r.quantity < 0 ? 'var(--grn-error)' : 'var(--grn-blue)' }}>{r.quantity}</td>
                                        <td style={td}>₹{r.price}</td>
                                        <td style={{ ...td, fontWeight:600, color: r.amount < 0 ? 'var(--grn-error)' : 'var(--grn-green)' }}>₹{Number(r.amount).toLocaleString('en-IN')}</td>
                                        <td style={td}>
                                            {r.is_credit_note
                                                ? <span style={{ fontSize:'10px', fontWeight:700, padding:'2px 7px', borderRadius:'4px', background:'var(--grn-pink-tint)', color:'var(--grn-error)' }}>Credit</span>
                                                : <span style={{ fontSize:'10px', fontWeight:700, padding:'2px 7px', borderRadius:'4px', background:'var(--grn-green-tint)', color:'var(--grn-green)' }}>Sale</span>
                                            }
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>
        </div>
    );
}

const cardS = { background:'var(--grn-surface)', borderRadius:'16px', padding:'20px', boxShadow:'var(--grn-shadow-sm)', border:'1px solid var(--grn-border)' };
const tableS = { width:'100%', borderCollapse:'collapse', fontSize:'13px', fontFamily: FONT };
const thS = { background:'var(--grn-accent-surface)', color:'#fff', padding:'10px 14px', textAlign:'center', fontSize:'12px', fontWeight:600, whiteSpace:'nowrap' };
const td    = { padding:'11px 14px', borderBottom:'1px solid var(--grn-border)', color:'var(--grn-text)', verticalAlign:'middle', whiteSpace:'nowrap', textAlign:'center' };
const dlBtn = { padding:'7px 16px', background:'var(--grn-accent-surface)', color:'#fff', border:'none', borderRadius:'7px', fontSize:'13px', fontWeight:600, cursor:'pointer' };
const inputS = { padding:'9px 14px', border:'1.5px solid var(--grn-border)', borderRadius:'8px', fontSize:'14px', outline:'none', width:'260px', fontFamily: FONT };
